import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCircleCheck, faCircleXmark, faFileLines, faMoneyBillWave,
  faPenToSquare, faTrash, faShareNodes
} from '@fortawesome/free-solid-svg-icons';
import { useAuth } from '../context/AuthContext';
import { 
  getReimbursements, 
  approveReimbursement, 
  rejectReimbursement, 
  payReimbursement,
  deleteReimbursement,
  getMinistries 
} from '../services/api';
import { formatAccountingCurrency, formatDate } from '../utils/formatters';
import './AdminReimbursements.css';

const AdminReimbursements = () => {
  const { isAdmin } = useAuth();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [reimbursements, setReimbursements] = useState([]);
  const [ministries, setMinistries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedReimbursement, setSelectedReimbursement] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [rejectionReason, setRejectionReason] = useState('');
  const [shareOptions, setShareOptions] = useState({
    includeInvoice: true,
    includePDF: false
  });
  const [filters, setFilters] = useState({
    status: searchParams.get('status') || '',
    ministry: searchParams.get('ministry') || ''
  });
  const [stats, setStats] = useState({
    pending: 0,
    approved: 0,
    rejected: 0,
    paid: 0,
    totalAmount: 0
  });

  useEffect(() => {
    if (!isAdmin) {
      navigate('/');
      return;
    }
    loadData();
  }, [isAdmin, navigate, filters]);

  const loadData = async () => {
    try {
      setLoading(true);
      const [reimbursementsRes, ministriesRes] = await Promise.all([
        getReimbursements(filters),
        getMinistries(new Date().getFullYear())
      ]);
      
      setReimbursements(reimbursementsRes.data);
      setMinistries(ministriesRes.data);
      calculateStats(reimbursementsRes.data);
    } catch (error) {
      console.error('Erro ao carregar dados:', error);
    } finally {
      setLoading(false);
    }
  };

  const calculateStats = (data) => {
    const stats = {
      pending: data.filter(r => r.status === 'pending').length,
      approved: data.filter(r => r.status === 'approved').length,
      rejected: data.filter(r => r.status === 'rejected').length,
      paid: data.filter(r => r.status === 'paid').length,
      totalAmount: data
        .filter(r => r.status === 'paid')
        .reduce((sum, r) => sum + r.amount, 0)
    };
    setStats(stats);
  };

  const handleFilterChange = (key, value) => {
    const newFilters = { ...filters, [key]: value };
    setFilters(newFilters);
    
    // Update URL
    const params = new URLSearchParams();
    Object.keys(newFilters).forEach(k => {
      if (newFilters[k]) params.set(k, newFilters[k]);
    });
    setSearchParams(params);
  };

  const handleViewDetails = (reimbursement) => {
    setSelectedReimbursement(reimbursement);
    setShowModal(true);
  };

  const handleClone = (reimbursement) => {
    navigate(`/request-reimbursement/${reimbursement.ministry._id}?cloneFrom=${reimbursement._id}`);
  };

  const handleApprove = async (id) => {
    if (!window.confirm('Tem certeza que deseja aprovar este reembolso?')) {
      return;
    }

    try {
      await approveReimbursement(id);
      loadData();
      setShowModal(false);
    } catch (error) {
      console.error('Erro ao aprovar reembolso:', error);
      alert(error.response?.data?.message || 'Erro ao aprovar reembolso.');
    }
  };

  const handleReject = async (id) => {
    if (!rejectionReason.trim()) {
      alert('Por favor, forneça um motivo para a reprovação.');
      return;
    }

    try {
      await rejectReimbursement(id, rejectionReason);
      loadData();
      setShowModal(false);
      setRejectionReason('');
    } catch (error) {
      console.error('Erro ao rejeitar reembolso:', error);
      alert(error.response?.data?.message || 'Erro ao rejeitar reembolso.');
    }
  };

  const handlePay = async (id) => {
    if (!window.confirm('Confirmar que este reembolso foi pago?')) {
      return;
    }

    try {
      await payReimbursement(id);
      loadData();
      setShowModal(false);
    } catch (error) {
      console.error('Erro ao marcar como pago:', error);
      alert(error.response?.data?.message || 'Erro ao marcar como pago.');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Tem certeza que deseja deletar este reembolso?\n\nO arquivo anexado também será removido do servidor.\n\nEsta ação não pode ser desfeita.')) {
      return;
    }

    try {
      await deleteReimbursement(id);
      loadData();
      setShowModal(false);
      alert('Reembolso deletado com sucesso!');
    } catch (error) {
      console.error('Erro ao deletar reembolso:', error);
      alert(error.response?.data?.message || 'Erro ao deletar reembolso.');
    }
  };

  const getStatusBadgeClass = (status) => {
    switch (status) {
      case 'pending': return 'badge-pending';
      case 'approved': return 'badge-approved';
      case 'rejected': return 'badge-rejected';
      case 'paid': return 'badge-paid';
      case 'paid-pending-invoice': return 'badge-paid-pending-invoice';
      default: return '';
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case 'pending': return 'Pendente';
      case 'approved': return 'Aprovado';
      case 'rejected': return 'Rejeitado';
      case 'paid': return 'Pago';
      case 'paid-pending-invoice': return 'Pago - Pendendo NF';
      default: return status;
    }
  };

  const handleShareWhatsApp = (reimbursement) => {
    // Gera a mensagem formatada
    const message = `*SOLICITAÇÃO DE REEMBOLSO APROVADA*

*Ministério:* ${reimbursement.ministry?.name}
*Solicitante:* ${reimbursement.requester.name}
*CPF/CNPJ:* ${reimbursement.requester.cpfCnpj}
*Chave PIX:* ${reimbursement.pixKey}
*Valor:* ${formatAccountingCurrency(reimbursement.amount)}

*Descrição:* ${reimbursement.description}

*Status:* ✅ Aprovado pela Igreja
*Data Aprovação:* ${formatDate(reimbursement.approvedAt)}
*Aprovado por:* ${reimbursement.approvedBy?.name || 'N/A'}

_Nota Fiscal:_ ${reimbursement.fileUrl}

Aguardando processamento do pagamento.`;

    // Codifica a mensagem para URL
    const encodedMessage = encodeURIComponent(message);
    
    // Abre o WhatsApp Web
    const whatsappUrl = `https://web.whatsapp.com/send?text=${encodedMessage}`;
    window.open(whatsappUrl, '_blank');
  };

  if (loading) {
    return <div className="loading">Carregando...</div>;
  }

  return (
    <div className="admin-page">
      <div className="container">
        <div className="page-header">
          <h1>Administração de Reembolsos</h1>
        </div>

        {/* Stats */}
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Pendentes</h3>
            <p className="stat-value text-warning">{stats.pending}</p>
          </div>
          <div className="stat-card">
            <h3>Aprovados</h3>
            <p className="stat-value text-success">{stats.approved}</p>
          </div>
          <div className="stat-card">
            <h3>Rejeitados</h3>
            <p className="stat-value text-danger">{stats.rejected}</p>
          </div>
          <div className="stat-card">
            <h3>Pagos (Total)</h3>
            <p className="stat-value text-primary">{formatAccountingCurrency(stats.totalAmount)}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-section card">
          <div className="filters-grid">
            <div className="form-group">
              <label className="form-label">Status</label>
              <select
                className="form-control"
                value={filters.status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
              >
                <option value="">Todos</option>
                <option value="pending">Pendentes</option>
                <option value="approved">Aprovados</option>
                <option value="rejected">Rejeitados</option>
                <option value="paid">Pagos</option>
              </select>
            </div>

            <div className="form-group">
              <label className="form-label">Ministério</label>
              <select
                className="form-control"
                value={filters.ministry}
                onChange={(e) => handleFilterChange('ministry', e.target.value)}
              >
                <option value="">Todos</option>
                {ministries.map((ministry) => (
                  <option key={ministry._id} value={ministry._id}>
                    {ministry.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        {reimbursements.length === 0 ? (
          <div className="card empty-state">
            <p>Nenhum reembolso encontrado com os filtros selecionados.</p>
          </div>
        ) : (
          <div className="table-container">
            <div className="table-responsive">
              <table>
                <thead>
                  <tr>
                    <th>Data</th>
                    <th>Ministério</th>
                    <th>Solicitante</th>
                    <th className="text-right">Valor</th>
                    <th className="text-center">Status</th>
                    <th className="text-center">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {reimbursements.map((reimbursement) => (
                    <tr key={reimbursement._id}>
                      <td>{formatDate(reimbursement.createdAt)}</td>
                      <td>{reimbursement.ministry?.name || 'N/A'}</td>
                      <td>
                        <strong>{reimbursement.requester.name}</strong>
                        <br />
                        <span className="text-muted text-sm">{reimbursement.requester.cpfCnpj}</span>
                      </td>
                      <td className="text-right text-negative">
                        <strong>{formatAccountingCurrency(reimbursement.amount)}</strong>
                      </td>
                      <td className="text-center">
                        <span className={`badge ${getStatusBadgeClass(reimbursement.status)}`}>
                          {getStatusLabel(reimbursement.status)}
                        </span>
                      </td>
                      <td>
                        <div className="action-buttons">
                          <button
                            onClick={() => handleViewDetails(reimbursement)}
                            className="btn btn-sm btn-primary"
                          >
                            Ver
                          </button>
                          <button
                            onClick={() => handleClone(reimbursement)}
                            className="btn btn-sm btn-secondary"
                            title="Clonar pedido"
                          >
                            <i className="fas fa-copy"></i> Clonar
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Modal */}
        {showModal && selectedReimbursement && (
          <div className="modal-overlay" onClick={() => setShowModal(false)}>
            <div className="modal-content modal-large" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Detalhes do Reembolso</h2>
                <button className="modal-close" onClick={() => setShowModal(false)}>
                  ×
                </button>
              </div>

              <div className="modal-body">
                <div className="detail-grid">
                  <div className="detail-item">
                    <label>Ministério</label>
                    <p>{selectedReimbursement.ministry?.name}</p>
                  </div>

                  <div className="detail-item">
                    <label>Status</label>
                    <p>
                      <span className={`badge ${getStatusBadgeClass(selectedReimbursement.status)}`}>
                        {getStatusLabel(selectedReimbursement.status)}
                      </span>
                    </p>
                  </div>

                  <div className="detail-item">
                    <label>Solicitante</label>
                    <p>{selectedReimbursement.requester.name}</p>
                  </div>

                  <div className="detail-item">
                    <label>CPF/CNPJ</label>
                    <p>{selectedReimbursement.requester.cpfCnpj}</p>
                  </div>

                  <div className="detail-item">
                    <label>Chave PIX</label>
                    <p>{selectedReimbursement.pixKey}</p>
                  </div>

                  <div className="detail-item">
                    <label>Valor</label>
                    <p className="text-negative">
                      <strong>{formatAccountingCurrency(selectedReimbursement.amount)}</strong>
                    </p>
                  </div>

                  <div className="detail-item full-width">
                    <label>Descrição</label>
                    <p>{selectedReimbursement.description}</p>
                  </div>

                  <div className="detail-item full-width">
                    <label>Comprovante da Despesa</label>
                    <a
                      href={selectedReimbursement.fileUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="btn btn-secondary btn-sm"
                    >
                      Ver Arquivo
                    </a>
                  </div>

                  {selectedReimbursement.paymentProofUrl && (
                    <div className="detail-item full-width">
                      <label>Comprovante de Pagamento</label>
                      <div className="payment-proof-info">
                        <span className="payment-proof-badge"><FontAwesomeIcon icon={faCircleCheck} /> Comprovante Anexado</span>
                        <a
                          href={selectedReimbursement.paymentProofUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="btn btn-success btn-sm"
                        >
                          <FontAwesomeIcon icon={faFileLines} /> Ver Comprovante de Pagamento
                        </a>
                      </div>
                    </div>
                  )}

                  {/* Audit Trail Section */}
                  <div className="detail-item full-width">
                    <label>Histórico de Ações</label>
                    <div className="audit-trail">
                      <div className="audit-item">
                        <div className="audit-icon audit-icon-created"><FontAwesomeIcon icon={faPenToSquare} /></div>
                        <div className="audit-content">
                          <strong>Criado por:</strong> {selectedReimbursement.createdBy?.name || 'N/A'}
                          <br />
                          <span className="text-muted text-sm">
                            {formatDate(selectedReimbursement.createdAt)}
                          </span>
                        </div>
                      </div>

                      {selectedReimbursement.approvedBy && (
                        <div className="audit-item">
                          <div className="audit-icon audit-icon-approved"><FontAwesomeIcon icon={faCircleCheck} /></div>
                          <div className="audit-content">
                            <strong>Aprovado por:</strong> {selectedReimbursement.approvedBy.name}
                            <br />
                            <span className="text-muted text-sm">
                              {formatDate(selectedReimbursement.approvedAt)}
                            </span>
                          </div>
                        </div>
                      )}

                      {selectedReimbursement.status === 'paid' && selectedReimbursement.paidAt && (
                        <div className="audit-item">
                          <div className="audit-icon audit-icon-paid"><FontAwesomeIcon icon={faMoneyBillWave} /></div>
                          <div className="audit-content">
                            <strong>Pago em:</strong> {formatDate(selectedReimbursement.paidAt)}
                          </div>
                        </div>
                      )}

                      {selectedReimbursement.status === 'rejected' && (
                        <div className="audit-item">
                          <div className="audit-icon audit-icon-rejected"><FontAwesomeIcon icon={faCircleXmark} /></div>
                          <div className="audit-content">
                            <strong>Rejeitado por:</strong> {selectedReimbursement.approvedBy?.name || 'N/A'}
                            <br />
                            <span className="text-muted text-sm">
                              {formatDate(selectedReimbursement.updatedAt)}
                            </span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {selectedReimbursement.status === 'rejected' && selectedReimbursement.rejectionReason && (
                    <div className="detail-item full-width">
                      <label>Motivo da Reprovação</label>
                      <div className="alert alert-danger">
                        {selectedReimbursement.rejectionReason}
                      </div>
                    </div>
                  )}

                  {selectedReimbursement.status === 'pending' && (
                    <div className="detail-item full-width">
                      <label>Motivo da Reprovação (opcional)</label>
                      <textarea
                        className="form-control"
                        value={rejectionReason}
                        onChange={(e) => setRejectionReason(e.target.value)}
                        placeholder="Digite o motivo caso deseje reprovar..."
                        rows="3"
                      />
                    </div>
                  )}
                </div>
              </div>

              <div className="modal-footer">
                <button
                  className="btn btn-danger"
                  onClick={() => handleDelete(selectedReimbursement._id)}
                  style={{ marginRight: 'auto' }}
                  title="Deletar permanentemente este reembolso e seus arquivos"
                >
                  <FontAwesomeIcon icon={faTrash} /> Deletar
                </button>

                <button className="btn btn-secondary" onClick={() => setShowModal(false)}>
                  Fechar
                </button>
                
                {(selectedReimbursement.status === 'approved' || selectedReimbursement.status === 'paid') && (
                  <button
                    className="btn btn-success"
                    onClick={() => handleShareWhatsApp(selectedReimbursement)}
                    title="Enviar os dados do reembolso por WhatsApp"
                  >
                    <FontAwesomeIcon icon={faShareNodes} /> Compartilhar via WhatsApp
                  </button>
                )}
                
                {selectedReimbursement.status === 'pending' && (
                  <>
                    <button
                      className="btn btn-danger"
                      onClick={() => handleReject(selectedReimbursement._id)}
                      title="Reprovar a solicitação (informe o motivo acima)"
                    >
                      <FontAwesomeIcon icon={faCircleXmark} /> Reprovar
                    </button>
                    <button
                      className="btn btn-success"
                      onClick={() => handleApprove(selectedReimbursement._id)}
                      title="Aprovar a solicitação para pagamento"
                    >
                      <FontAwesomeIcon icon={faCircleCheck} /> Aprovar
                    </button>
                  </>
                )}

                {selectedReimbursement.status === 'approved' && (
                  <button
                    className="btn btn-primary"
                    onClick={() => handlePay(selectedReimbursement._id)}
                    title="Registrar que o pagamento foi efetuado"
                  >
                    <FontAwesomeIcon icon={faMoneyBillWave} /> Marcar como Pago
                  </button>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminReimbursements;
